import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { ArrowLeft, Copy, Users, TrendingUp, Flame } from 'lucide-react';
import Badges from '@/components/Badges';

export default function TraderProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [following, setFollowing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [alloc, setAlloc] = useState('10');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const p = await api.get(`/traders/${id}`);
    setProfile(p.data);
    if (user) {
      const f = await api.get('/traders/following/mine');
      setFollowing(f.data.following.find(x => x.leader_id === id) || null);
    }
  };
  useEffect(() => { load(); }, [id, user?.user_id]);

  const follow = async () => {
    setBusy(true);
    try {
      await api.post(`/traders/follow/${id}`, { alloc_pct: parseFloat(alloc) });
      toast.success(`Now copying ${profile.name}`);
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const unfollow = async () => {
    try { await api.delete(`/traders/follow/${id}`); toast.success('Unfollowed'); load(); }
    catch (e) { toast.error('Failed'); }
  };

  if (!profile) return <div className="max-w-6xl mx-auto px-6 py-10 text-[#848E9C]">Loading…</div>;

  const roiUp = profile.roi_pct >= 0;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <Link to="/traders" className="text-[#FCD535] text-sm hover:underline inline-flex items-center gap-1 mb-6" data-testid="back-to-traders">
        <ArrowLeft className="w-4 h-4" /> Back to traders
      </Link>

      {/* Header card */}
      <div className="bg-gradient-to-br from-[#181A20] to-[#0B0E11] border border-[#2B3139] p-6 sm:p-8 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            {profile.picture ? (
              <img src={profile.picture} alt="" className="w-16 h-16 rounded-full border border-[#2B3139]" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-[#2B3139] grid place-items-center text-white text-2xl font-bold">
                {profile.name?.[0]?.toUpperCase() || '?'}
              </div>
            )}
            <div>
              <h1 className="font-display text-white text-3xl font-bold tracking-tighter">{profile.name}</h1>
              <div className="text-xs text-[#848E9C] mt-1 font-mono">{profile.user_id}</div>
              {profile.badges?.length > 0 && (
                <div className="mt-3">
                  <Badges badges={profile.badges} size="md" />
                </div>
              )}
              {profile.season_badges?.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 mt-3" data-testid="season-badges">
                  {profile.season_badges.map((sb, i) => (
                    <div key={i}
                      className="flex items-center gap-1 px-2 py-1 border border-[#FCD535]/40 bg-[#FCD535]/10 rounded-sm text-xs"
                      title={`Season ${sb.season_id + 1} · #${sb.place} finish · ${sb.streak}🔥 streak`}
                      data-testid={`season-badge-${i}`}>
                      <span className="text-lg leading-none">{sb.medal}</span>
                      <span className="text-[#FCD535] font-mono">S{sb.season_id + 1}</span>
                    </div>
                  ))}
                </div>
              )}
              {(profile.battle_correct_streak || 0) > 0 && (
                <div className="mt-2 flex items-center gap-1 text-xs text-[#F6465D] font-mono">
                  <Flame className="w-3.5 h-3.5" /> {profile.battle_correct_streak}-battle streak
                </div>
              )}
              <div className="flex gap-4 mt-3 text-xs text-[#B7BDC6]">
                <span><span className="text-[#EAECEF] font-mono">{profile.followers}</span> followers</span>
                <span><span className="text-[#EAECEF] font-mono">{profile.trades_count}</span> trades</span>
                <span className="text-[#0ECB81]"><span className="font-mono">{profile.buys}</span> buys</span>
                <span className="text-[#F6465D]"><span className="font-mono">{profile.sells}</span> sells</span>
              </div>
            </div>
          </div>
          {user && user.user_id !== profile.user_id && (
            following ? (
              <Button onClick={unfollow} className="bg-[#2B3139] hover:bg-[#323842] text-white rounded-sm" data-testid="profile-unfollow-btn">
                <Copy className="w-4 h-4 mr-1.5" /> Copying · {following.alloc_pct}% · Unfollow
              </Button>
            ) : (
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold rounded-sm" data-testid="profile-follow-btn">
                    <Copy className="w-4 h-4 mr-1.5" /> Copy Trade
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm">
                  <DialogHeader><DialogTitle>Copy {profile.name}</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <Label className="text-[#B7BDC6] text-xs uppercase tracking-wider">Allocation per trade (%)</Label>
                    <Input type="number" value={alloc} onChange={e=>setAlloc(e.target.value)}
                      className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono focus-visible:ring-[#FCD535]/40 focus-visible:border-[#FCD535]"
                      data-testid="profile-alloc-input" />
                    <Button onClick={follow} disabled={busy} className="w-full bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm font-semibold" data-testid="profile-confirm-follow">
                      {busy ? 'Starting…' : 'Start Copying'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-8 border-t border-[#2B3139] pt-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#848E9C]">ROI · All Time</div>
            <div className={`font-mono text-3xl mt-1 ${roiUp ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>{roiUp && '+'}{profile.roi_pct}%</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-[#848E9C]">Portfolio Value</div>
            <div className="font-mono text-3xl mt-1 text-white">${profile.portfolio_value.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-[#848E9C]">Volume</div>
            <div className="font-mono text-3xl mt-1 text-white">${profile.volume.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-[#848E9C]">Copiers</div>
            <div className="font-mono text-3xl mt-1 text-[#FCD535]">{profile.followers}</div>
          </div>
        </div>
      </div>

      {/* PnL Curve */}
      <div className="border border-[#2B3139] bg-[#181A20] p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-4 h-4 text-[#FCD535]" />
          <div className="text-white font-semibold">Portfolio PnL Curve</div>
        </div>
        <div className="h-[300px]" data-testid="trader-pnl-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={profile.pnl_curve}>
              <defs>
                <linearGradient id="tgrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={roiUp ? '#0ECB81' : '#F6465D'} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={roiUp ? '#0ECB81' : '#F6465D'} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="t" hide />
              <YAxis domain={['dataMin', 'dataMax']} tick={{ fill: '#5E6673', fontSize: 10, fontFamily: 'JetBrains Mono' }} axisLine={false} tickLine={false} width={70} tickFormatter={(v) => `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`} />
              <Tooltip contentStyle={{ background: '#0B0E11', border: '1px solid #2B3139', borderRadius: 0, fontFamily: 'JetBrains Mono' }}
                labelFormatter={(t) => new Date(t).toLocaleString()}
                formatter={(v) => [`$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`, 'Value']} />
              <Area dataKey="v" stroke={roiUp ? '#0ECB81' : '#F6465D'} strokeWidth={2} fill="url(#tgrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Trades */}
      <h2 className="text-white text-xl font-semibold mb-3">Trade History ({profile.recent_trades.length})</h2>
      <div className="border border-[#2B3139] bg-[#181A20]">
        <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-[#848E9C] border-b border-[#2B3139]">
          <div className="col-span-2">Side</div>
          <div className="col-span-2">Symbol</div>
          <div className="col-span-3 text-right">Amount</div>
          <div className="col-span-2 text-right">Price</div>
          <div className="col-span-3 text-right">Total · Date</div>
        </div>
        {profile.recent_trades.length === 0 ? (
          <div className="p-8 text-center text-[#848E9C] text-sm">No trades yet.</div>
        ) : profile.recent_trades.map(t => (
          <div key={t.trade_id} className="grid grid-cols-12 px-4 py-3 border-b border-[#2B3139]/60 items-center text-sm">
            <div className={`col-span-2 uppercase text-xs font-semibold ${t.side === 'buy' ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>{t.side}</div>
            <div className="col-span-2 text-white">{t.symbol}</div>
            <div className="col-span-3 text-right font-mono text-[#EAECEF]">{t.amount.toFixed(6)}</div>
            <div className="col-span-2 text-right font-mono text-[#B7BDC6]">${t.price.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
            <div className="col-span-3 text-right">
              <div className="font-mono text-white">${t.total_usd.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
              <div className="text-[#5E6673] text-xs">{new Date(t.created_at).toLocaleString()}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
